import { filter, fromEvent, map, Observable, switchMap, take, tap } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { getBrand } from '../../../utils/getBrand';
import { getBoldChatEnv } from '../../../utils/getBoldChatEnv';
import { getCustomerTrackingId } from '../../../utils/getAdobeCustomerTrackingId';

@Injectable({
  providedIn: 'root',
})
export class GenesysDxFallbackService {
  private liveChatApiFrame;
  private frameOrigin = 'https://api.boldchat.com';
  private iframeId = 'bc-api-call-iframe';
  private queueApiKey = '';

  constructor(private httpClient: HttpClient) {}

  createLiveChatIFrame = ({
    firstName = '',
    lastName = '',
    language = 'en',
    chatKey = '',
  } = {}): Observable<any> => {
    return this.httpClient
      .get(`configurations/fallback-queue-api-keys/${getBrand().name}.json`)
      .pipe(
        tap((res) => {
          this.queueApiKey = res[0][getBoldChatEnv()][language];
          this.createLiveChatIframeIfNotExists(this.queueApiKey);
        }),
        switchMap(() => fromEvent<MessageEvent>(window, 'message')),
        filter((event: MessageEvent) => event.origin === this.frameOrigin),
        map((event) => {
          return JSON.parse(event?.data);
        }),
        tap((data) => {
          if (data.method === 'loaded') {
            const params = {
              Language: language,
              IncludeBrandingValues: false,
              SkipPreChat: true,
              Data: JSON.stringify({
                name: firstName + ' ' + lastName,
                custom_custtrackingid: getCustomerTrackingId(),
              }),
              Secured: true,
              ChatUrl: document.location.href,
              auth: window.btoa(this.queueApiKey), // encode chatApiKey
            };
            let method = 'createChat';
            if (chatKey) {
              method = 'startChat';
              params['ChatKey'] = chatKey;
            }
            this.callBoldChatApiMethod(method, params);
          }
        }),
        filter((data) => {
          this.appendQueueApiKey(data);
          return data.method !== 'loaded' && data.result;
        }),
        take(1)
      );
  };

  finishChat = (
    chatKey = '',
    clientId = '',
    queueApiKey = ''
  ): Observable<any> => {
    const params = {
      ClientID: clientId,
      ChatKey: chatKey,
      auth: window.btoa(queueApiKey), // encode chatApiKey
    };
    this.createLiveChatIframeIfNotExists(queueApiKey);
    this.callBoldChatApiMethod('finishChat', params);

    return fromEvent<MessageEvent>(window, 'message').pipe(
      filter((event: MessageEvent) => event.origin === this.frameOrigin),
      map((event) => {
        return JSON.parse(event?.data);
      }),
      tap((data) => {
        if (data.method === 'loaded') {
          this.callBoldChatApiMethod('finishChat', params);
        } else if (data.result.Status == 'success') {
          this.removeLiveChatIframe();
        }
      }),
      filter((data) => {
        return data.method !== 'loaded' && data.result;
      }),
      take(1)
    );
  };

  createLiveChatIframeIfNotExists(queueApiKey: string): void {
    if (!this.liveChatApiFrame) {
      const accountId = queueApiKey?.split(':')[0];
      const iframeSrc = `${this.frameOrigin}/aid/${accountId}/ext/api/apiframe.html`;
      document.querySelector(`#${this.iframeId}`)?.remove();
      this.liveChatApiFrame = document.createElement('iframe');
      this.liveChatApiFrame.style.display = 'none';
      this.liveChatApiFrame.hidden = true;
      this.liveChatApiFrame.src = iframeSrc;
      this.liveChatApiFrame.id = this.iframeId;
      document.body.appendChild(this.liveChatApiFrame);
    }
  }

  removeLiveChatIframe(): void {
    document.querySelector(`#${this.iframeId}`)?.remove();
    this.liveChatApiFrame = null;
  }

  callBoldChatApiMethod(method: string, params: any): void {
    this.liveChatApiFrame.contentWindow.postMessage(
      JSON.stringify({
        method: method,
        params: params,
      }),
      this.frameOrigin
    );
  }

  appendQueueApiKey(data: any): void {
    if (data?.result) {
      data.result.queueApiKey = this.queueApiKey;
    }
  }
}
